/**
 * `products.specifications.pricing` — "기본 + 추가일 + 패키지" 요금 정본.
 *
 * admin 은 기본가·추가일·패키지를 저장하고 기준가 행 하나만 유지한다.
 * 상세 화면·장바구니·결제 검증은 이 원본으로 예약 기간 총액을 계산한다.
 *
 * 저장 형태(admin 이 쓰는 계약 그대로):
 * ```json
 * {
 *   "model": "base_plus_extra",
 *   "unitHours": 24,
 *   "subProducts": {
 *     "0": { "base": 9500, "extraDay": 6000,
 *            "packages": [{ "days": 7, "price": 40000 }] }
 *   }
 * }
 * ```
 * - `subProducts` 의 키 = `sub_display_order` 문자열.
 * - 값은 **공급가**(마크업 없음, 상품 통화). 고객에게 보이는 값은 반드시
 *   `day_prices` 와 같은 마크업을 거쳐야 한다 → `markUpPricingSource`
 *   (서버 경계 `lib/api/products/detail.ts` 에서 1회만 적용, 클라이언트는 표시만).
 * - 대여일수는 픽업일과 반납일을 포함한 달력 날짜 수. 기본 포함 일수는
 *   세부 상품의 `baseDays`(1 또는 2)이며 기존 데이터는 2로 읽는다.
 */

import { getDailyV2RateForDays, type DailyTier } from "@/lib/utils/daily-matrix";
import { getSeasonalDailyV2Rates } from "@/lib/utils/pricing";
import { applyMarkupToUnit } from "@/lib/utils/pricing-model";
import type { PricingSeason } from "@/types";
import type { SupportedCurrency } from "@/lib/utils/currency";

/** 패키지 1건 — `days` 일짜리 정액 요금. */
export interface PricingPackage {
  days: number;
  price: number;
}

/** 세부 상품 1개의 요금 원천. */
export interface SubProductPricingSource {
  /** 기본 요금에 포함되는 달력 일수. 기존 상품은 2일. */
  baseDays?: 1 | 2;
  /** baseDays 날짜에 적용되는 기본 요금. 항상 > 0. */
  base: number;
  /** 기본 포함 일수 뒤 날짜별 추가 요금. 없거나 잘못된 값이면 0. */
  extraDay: number;
  /** `days` 오름차순, 유효한 것만 (days > 0 && price > 0). */
  packages: PricingPackage[];
}

/** 파싱된 요금 원천. `subProducts` 키 = `sub_display_order` 문자열. */
export interface PricingSource {
  model: "base_plus_extra";
  /** 단위 시간. 값이 없거나 이상하면 24. */
  unitHours: number;
  subProducts: Record<string, SubProductPricingSource>;
}

/**
 * 마크업이 적용된 요금 원천 — 모양은 같지만 **표시가**라는 뜻의 별칭.
 * 서버가 이 타입으로 내려주고, 클라이언트는 다시 곱하지 않는다(이중 마크업 금지).
 */
export type MarkedUpPricingSource = PricingSource;

/** The stored policy, not a rounded per-day tier, determines the rental total. */
export function totalForDays(sub: SubProductPricingSource, days: number): number {
  const duration = Math.max(1, Math.floor(days));
  const baseDays = sub.baseDays === 1 ? 1 : 2;
  let best = sub.base + sub.extraDay * Math.max(0, duration - baseDays);
  const packages = sub.packages
    .filter((p) => Number.isInteger(p.days) && p.days >= 2 && p.price > 0)
    .sort((a, b) => b.days - a.days);
  const search = (index: number, remaining: number, cost: number, used: boolean): void => {
    if (cost >= best) return;
    if (index === packages.length) {
      if (used) best = Math.min(best, cost + sub.extraDay * remaining);
      return;
    }
    const pkg = packages[index];
    const max = Math.floor(remaining / pkg.days) + 1;
    for (let count = 0; count <= max; count++) {
      const next = cost + count * pkg.price;
      if (next >= best) break;
      const rest = remaining - count * pkg.days;
      if (rest <= 0) {
        if (used || count > 0) best = Math.min(best, next);
        break;
      }
      search(index + 1, rest, next, used || count > 0);
    }
  };
  search(0, duration, 0, false);
  return best;
}

/** Returns null for manually entered daily-rate products or missing sub policies. */
export function policyTotalForDays(
  specifications: unknown,
  subDisplayOrder: number,
  days: number,
): number | null {
  const sub = parsePricingSource(specifications)?.subProducts[String(subDisplayOrder)];
  return sub && Number.isInteger(days) && days > 0 ? totalForDays(sub, days) : null;
}

/** Compatibility baseline for existing seasonal day-rate overrides. */
export function policySeasonBaseline(
  specifications: unknown,
  subDisplayOrder: number,
  days: number,
  currency: SupportedCurrency,
): { minDays: number; pricePerDay: number } | null {
  const minDays = Math.min(days, 60);
  const total = policyTotalForDays(specifications, subDisplayOrder, minDays);
  if (total == null) return null;
  const step = currency === "JPY" || currency === "KRW" ? 10 : 1;
  return { minDays, pricePerDay: Math.ceil(total / minDays / step - 1e-9) * step };
}

/** Customer total: mark up the policy total once, then apply seasonal day overrides. */
export function customerPolicyTotalForDays(args: {
  specifications: unknown;
  subDisplayOrder: number;
  days: number;
  currency: SupportedCurrency;
  markupRate?: number | null;
  pickupDate?: Date;
  markedTiers?: DailyTier[];
  markedSeasons?: PricingSeason[];
}): number | null {
  const supply = policyTotalForDays(args.specifications, args.subDisplayOrder, args.days);
  if (supply == null) return null;
  const up = markupFn(args.markupRate, args.currency);
  const baseline = policySeasonBaseline(
    args.specifications, args.subDisplayOrder, args.days, args.currency,
  );
  return applySeasonalPolicyAdjust(
    up(supply),
    baseline ? { minDays: baseline.minDays, pricePerDay: up(baseline.pricePerDay) } : null,
    args.days,
    args.subDisplayOrder,
    args.pickupDate,
    args.markedSeasons,
  );
}

/** 요율 → 단가 마크업 함수. 요율이 (0,1) 밖이면 원값 그대로(마크업 없음). */
function markupFn(
  rate: number | null | undefined,
  currency: SupportedCurrency,
): (value: number) => number {
  const r = rate ?? 0;
  return (value: number): number =>
    r > 0 && r < 1 ? applyMarkupToUnit(value, r, currency) : value;
}

/**
 * 고객 총액(마크업 반영)에 성수기 일자 재정의를 더한다 — 요율을 아는 서버 경로
 * (`customerPolicyTotalForDays`)와 요율 없이 표만 읽는 브라우저 경로
 * (`customerPolicyTotalFromTable`)가 **같은 함수**를 써서 숫자가 갈라지지 않는다.
 */
function applySeasonalPolicyAdjust(
  markedTotal: number,
  markedBaseline: DailyTier | null,
  days: number,
  subDisplayOrder: number,
  pickupDate: Date | undefined,
  markedSeasons: PricingSeason[] | undefined,
): number {
  let total = markedTotal;
  const tiers = markedBaseline ? [markedBaseline] : [];
  if (pickupDate && tiers.length && markedSeasons?.length) {
    const baseRate = getDailyV2RateForDays(days, tiers);
    const { rates } = getSeasonalDailyV2Rates(
      pickupDate, days, tiers, subDisplayOrder, markedSeasons,
    );
    total += rates.reduce((sum, value) => sum + value - baseRate, 0);
  }
  return total;
}

// ── 고객 요금표 (10-16) ────────────────────────────────────────────────────
// 브라우저에는 **요율도 공급가도** 보내지 않는다. 서버가 요율로 미리 계산한 고객가
// 표만 싣고, 화면은 그 표를 읽는다. 표의 각 칸은 `customerPolicyTotalForDays` 와
// 같은 함수·같은 반올림으로 만든 값이라 상세·장바구니·결제 검증과 숫자가 같다.

/** 상세 화면 표의 최대 대여일수 — 예약 달력은 오늘+90일까지라 대여일수(포함)는 91일이 최대. 여유 포함. */
export const POLICY_TABLE_DETAIL_MAX_DAYS = 100;
/** 성수기 비교 기준 일가는 `min(일수, 60)` 일로만 계산된다(`policySeasonBaseline`). */
const SEASON_BASELINE_MAX_DAYS = 60;

/** 세부 상품 1개의 고객 요금표. */
export interface CustomerPolicySub {
  /** `totals[d-1]` = d일 대여 고객 총액(마크업 반영, 성수기 전). */
  totals: number[];
  /** `seasonBases[m-1]` = 성수기 재정의 비교 기준 일가(m = min(일수, 60), 마크업 반영). */
  seasonBases: number[];
}

/** 키 = `sub_display_order` 문자열 (pricingSource 와 같은 축). */
export type CustomerPolicyTable = Record<string, CustomerPolicySub>;

/**
 * 서버 전용 계산(순수 함수지만 요율을 받는다) — 원본 `specifications.pricing` + 요율 →
 * 고객 요금표. 정책이 없으면 null.
 */
export function buildCustomerPolicyTable(
  specifications: unknown,
  currency: SupportedCurrency,
  markupRate: number | null | undefined,
  maxDays: number = POLICY_TABLE_DETAIL_MAX_DAYS,
): CustomerPolicyTable | null {
  const source = parsePricingSource(specifications);
  if (!source) return null;
  const up = markupFn(markupRate, currency);
  const step = currency === "JPY" || currency === "KRW" ? 10 : 1;
  const table: CustomerPolicyTable = {};
  for (const [key, sub] of Object.entries(source.subProducts)) {
    const supplyTotals: number[] = [];
    for (let d = 1; d <= maxDays; d++) supplyTotals.push(totalForDays(sub, d));
    const seasonBases: number[] = [];
    for (let m = 1; m <= Math.min(maxDays, SEASON_BASELINE_MAX_DAYS); m++) {
      // policySeasonBaseline 과 같은 식(minDays = m ≤ 60).
      seasonBases.push(up(Math.ceil(supplyTotals[m - 1] / m / step - 1e-9) * step));
    }
    table[key] = { totals: supplyTotals.map(up), seasonBases };
  }
  return table;
}

/**
 * 브라우저용 — 고객 요금표에서 총액을 읽는다. `customerPolicyTotalForDays` 의 표 버전
 * (요율·공급가 없이 같은 숫자). 표 밖(정책 없음·세부 상품 없음·표 범위 밖 일수)은 null.
 */
export function customerPolicyTotalFromTable(args: {
  table: CustomerPolicyTable | null | undefined;
  subDisplayOrder: number;
  days: number;
  pickupDate?: Date;
  markedSeasons?: PricingSeason[];
}): number | null {
  const sub = args.table?.[String(args.subDisplayOrder)];
  if (!sub || !Number.isInteger(args.days) || args.days <= 0) return null;
  const total = sub.totals[args.days - 1];
  if (total == null) return null;
  const minDays = Math.min(args.days, SEASON_BASELINE_MAX_DAYS);
  const base = sub.seasonBases[minDays - 1];
  return applySeasonalPolicyAdjust(
    total,
    base == null ? null : { minDays, pricePerDay: base },
    args.days,
    args.subDisplayOrder,
    args.pickupDate,
    args.markedSeasons,
  );
}

/**
 * 목록·허브 카드용 기간 총액 — 세부 상품 중 최저(>0) 고객 총액을 일수별로.
 * `lib/mobility/pricing.periodTotal` 의 정책 경로와 같은 값. 값이 없으면 0.
 */
export function buildPolicyPeriodTotals(
  specifications: unknown,
  currency: SupportedCurrency,
  markupRate: number | null | undefined,
  days: readonly number[],
): Record<number, number> | null {
  const source = parsePricingSource(specifications);
  if (!source) return null;
  const up = markupFn(markupRate, currency);
  const out: Record<number, number> = {};
  for (const d of days) {
    if (!Number.isInteger(d) || d <= 0) continue;
    let best = 0;
    for (const sub of Object.values(source.subProducts)) {
      const value = up(totalForDays(sub, d));
      if (value > 0 && (best === 0 || value < best)) best = value;
    }
    out[d] = best;
  }
  return out;
}

/** 1..n 일수 목록. */
export function dayRange(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i + 1);
}

/**
 * `specifications` 에서 원본 요금(`pricing`, 공급가)만 뺀 사본 — 브라우저로 보내는 값.
 * 다른 키(하루당 옵션·편도 반납·제원 등)는 그대로 둔다. `pricing` 이 없으면 입력 그대로.
 */
export function stripSupplyPricing<T extends Record<string, unknown> | null | undefined>(
  specifications: T,
): T {
  if (!specifications || !("pricing" in specifications)) return specifications;
  const rest: Record<string, unknown> = { ...specifications };
  delete rest.pricing;
  return rest as T;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  // numeric 컬럼/JSON 이 문자열로 오는 경우까지만 허용 — 그 외 타입은 거부.
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parsePackages(value: unknown): PricingPackage[] {
  if (!Array.isArray(value)) return [];
  const packages: PricingPackage[] = [];
  for (const raw of value) {
    if (!isRecord(raw)) continue;
    const days = toFiniteNumber(raw.days);
    const price = toFiniteNumber(raw.price);
    // 잘못된 패키지는 **버린다** — 요약 전체를 숨기면 정상 행까지 잃는다.
    if (days == null || days <= 0 || price == null || price <= 0) continue;
    packages.push({ days, price });
  }
  packages.sort((a, b) => a.days - b.days);
  return packages;
}

/**
 * `products.specifications` 에서 요금 원천을 꺼낸다.
 *
 * `model !== "base_plus_extra"` 이거나 쓸 수 있는 세부 상품이 하나도 없으면
 * `null` — 호출측은 null 이면 **기존 화면 그대로** 그린다(무회귀).
 */
export function parsePricingSource(
  specifications: unknown,
): PricingSource | null {
  if (!isRecord(specifications)) return null;
  const pricing = specifications.pricing;
  if (!isRecord(pricing)) return null;
  if (pricing.model !== "base_plus_extra") return null;

  const unitHoursRaw = toFiniteNumber(pricing.unitHours);
  const unitHours = unitHoursRaw != null && unitHoursRaw > 0 ? unitHoursRaw : 24;

  const rawSubs = pricing.subProducts;
  if (!isRecord(rawSubs)) return null;

  const subProducts: Record<string, SubProductPricingSource> = {};
  for (const [key, value] of Object.entries(rawSubs)) {
    if (!isRecord(value)) continue;
    const base = toFiniteNumber(value.base);
    // base 가 없는 세부 상품은 요약을 그릴 수 없다 → 그 세부 상품만 제외.
    if (base == null || base <= 0) continue;
    const extraDayRaw = toFiniteNumber(value.extraDay);
    const extraDay = extraDayRaw != null && extraDayRaw > 0 ? extraDayRaw : 0;
    subProducts[key] = {
      baseDays: value.baseDays === 1 ? 1 : 2,
      base,
      extraDay,
      packages: parsePackages(value.packages),
    };
  }

  if (Object.keys(subProducts).length === 0) return null;
  return { model: "base_plus_extra", unitHours, subProducts };
}

/**
 * 모든 금액에 `up()` 을 적용한 **새 객체**를 반환한다(입력 불변).
 * `up` 은 `day_prices` 마크업과 **같은 함수**여야 한다 — 다르면 요약과
 * 합계(결제가)가 갈라진다.
 */
export function markUpPricingSource(
  source: PricingSource,
  up: (value: number) => number,
): MarkedUpPricingSource {
  const subProducts: Record<string, SubProductPricingSource> = {};
  for (const [key, sub] of Object.entries(source.subProducts)) {
    subProducts[key] = {
      baseDays: sub.baseDays,
      base: up(sub.base),
      // 0(추가 요금 없음)은 그대로 0 — 마크업을 태우면 의미가 바뀐다.
      extraDay: sub.extraDay > 0 ? up(sub.extraDay) : sub.extraDay,
      packages: sub.packages.map((pkg) => ({
        days: pkg.days,
        price: up(pkg.price),
      })),
    };
  }
  return { model: source.model, unitHours: source.unitHours, subProducts };
}

/**
 * 카드 대표가가 **며칠 묶음 요금**인가(10-15) — 기본+추가일 상품의 카드 금액은 세부 상품 중 가장
 * 싼 기본 요금이고, 그 기본 요금은 `baseDays` 일치다. 2일 이상이면 그 일수(카드가 "/ 1박 2일"을
 * 붙인다), 1일이거나 정책이 없으면 null(1일가라 단위를 붙이지 않는다).
 */
export function bundleDaysOf(specifications: unknown): number | null {
  const source = parsePricingSource(specifications);
  if (!source) return null;
  let cheapest: SubProductPricingSource | null = null;
  for (const sub of Object.values(source.subProducts)) {
    if (!cheapest || sub.base < cheapest.base) cheapest = sub;
  }
  const days = cheapest?.baseDays ?? 2;
  return cheapest && days >= 2 ? days : null;
}
